import { haversineDistanceKm } from './StoreDistance.js';
import { isStoreFiltersEmpty, matchesStoreFilters } from './StoreFilterLogic.js';

/**
 * Calcul pur (sans DOM, sans Mapbox) du cadrage de la carte de « Nos
 * revendeurs » autour d'un point précis (chantier store-locator-zoom-geoloc,
 * point 1, critères 1-7b, scénarios Z1-Z16).
 *
 * Fichier volontairement séparé de `MapboxCluster.js` (carte, DOM), sur le
 * même principe que `StoreDistance.js`, `StoreFilterLogic.js` et
 * `StoreOpeningLogic.js` : testable directement en Node, sans navigateur ni
 * build du thème. La distance est **celle de la liste** (`haversineDistanceKm`
 * de `StoreDistance.js`), pour que « les plus proches » de la carte soient
 * exactement ceux que la liste place en tête.
 *
 * Règle (décisions de Robin du 2026-10-01) :
 * - sans filtre : le point et les 5 magasins les plus proches ;
 * - avec au moins un filtre actif (type, service, « Ouvert maintenant ») :
 *   le point et le magasin correspondant le plus proche (évite un très grand
 *   dézoom dans une zone sans ce type de magasin) ;
 * - aucun magasin ne correspond : repli sur les 5 plus proches sans filtre
 *   (`noMatch: true` — la liste affiche alors « Aucun revendeur ne
 *   correspond », ce module ne décide que de l'emprise).
 * Aucune écriture, aucun stockage : fonctions pures.
 */

/** Nombre de magasins les plus proches retenus sans filtre. */
export const FRAMING_NEAREST_COUNT = 5;

/** Nombre de magasins correspondants retenus avec un filtre actif. */
export const FRAMING_NEAREST_COUNT_FILTERED = 1;

/** Zoom maximal du cadrage (niveau « quartier », critère 6). Les pastilles de regroupement restent groupées jusqu'à ce zoom inclus (`clusterMaxZoom` 14). */
export const FRAMING_MAX_ZOOM = 14;

/**
 * Types de lieu du service de recherche qui désignent un point précis
 * (liste blanche, critère 5) : adresse (avec ou sans numéro : une rue sans
 * numéro reste de type `address`), lieu précis (`poi`) et quartier
 * (`neighborhood`). Tout autre type (ville, code postal, département,
 * région, pays…) garde le comportement existant : cadrage sur la zone.
 */
export const PRECISE_PLACE_TYPES = ['address', 'poi', 'neighborhood'];

/**
 * @param  {string[]|string|undefined} placeType `place_type` d'un résultat de recherche.
 * @returns {boolean} Vrai si le résultat est un point précis (liste blanche).
 */
export function isPrecisePlaceType(placeType) {
  const types = Array.isArray(placeType) ? placeType : [placeType];
  return types.some((type) => PRECISE_PLACE_TYPES.includes(type));
}

const hasCoordinates = (item) => Number.isFinite(item?.lat) && Number.isFinite(item?.lng);

/**
 * Magasins les plus proches d'un point, triés par distance croissante
 * (égalités départagées par le titre, puis par l'ordre d'origine : tri
 * stable). Les magasins à égalité exacte de distance avec le dernier retenu
 * sont conservés aussi (« au moins » n : jamais un magasin au même endroit
 * laissé de côté au hasard de l'ordre).
 * @param  {Array<{lat: number, lng: number, title?: string}>} items
 * @param  {{lat: number, lng: number}} origin
 * @param  {number} count Nombre minimal de magasins à retenir.
 * @returns {Array} Magasins retenus, chacun avec `__distanceKm`.
 */
export function nearestStores(items, origin, count) {
  const ranked = (Array.isArray(items) ? items : [])
    .filter(hasCoordinates)
    .map((item, index) => ({
      item,
      index,
      distance: haversineDistanceKm(origin, { lat: item.lat, lng: item.lng }),
    }))
    .sort((a, b) => a.distance - b.distance
      || String(a.item.title ?? '').localeCompare(String(b.item.title ?? ''), 'fr')
      || a.index - b.index);

  if (count <= 0 || ranked.length === 0) {
    return [];
  }
  const limit = ranked[Math.min(count, ranked.length) - 1].distance;
  return ranked
    .filter(({ distance }, rank) => rank < count || distance <= limit)
    .map(({ item, distance }) => ({ ...item, __distanceKm: distance }));
}

/**
 * @typedef {object} Framing
 * @property {Array} stores Magasins retenus (avec `__distanceKm`), du plus proche au plus loin.
 * @property {{west: number, south: number, east: number, north: number}} bounds Emprise : le point et les magasins retenus.
 * @property {boolean} filtered Vrai si l'emprise porte sur le magasin correspondant le plus proche (filtre actif).
 * @property {boolean} noMatch Vrai si un filtre est actif mais qu'aucun magasin ne correspond (repli sur les 5 plus proches).
 */

/**
 * Calcule l'emprise du cadrage.
 * @param  {object} params
 * @param  {Array} params.items Tous les magasins de la page (pas seulement ceux de la zone visible).
 * @param  {{lat: number, lng: number}} params.origin Point de référence (position acceptée ou recherche précise).
 * @param  {object} [params.filters] Filtres actifs (`StoreFilterLogic.js`).
 * @param  {Date} [params.now] Heure de référence pour « Ouvert maintenant ».
 * @returns {Framing|null} `null` si le point est invalide ou s'il n'y a aucun magasin localisé.
 */
export function computeFraming({ items, origin, filters = null, now = new Date() }) {
  if (!origin || !Number.isFinite(origin.lat) || !Number.isFinite(origin.lng)) {
    return null;
  }
  const located = (Array.isArray(items) ? items : []).filter(hasCoordinates);
  if (located.length === 0) {
    return null;
  }

  const filtersActive = !isStoreFiltersEmpty(filters);
  let stores = [];
  let noMatch = false;
  let filtered = false;

  if (filtersActive) {
    const matching = located.filter((item) => matchesStoreFilters(item, filters, now));
    if (matching.length > 0) {
      stores = nearestStores(matching, origin, FRAMING_NEAREST_COUNT_FILTERED);
      filtered = true;
    } else {
      noMatch = true;
    }
  }
  if (!filtered) {
    stores = nearestStores(located, origin, FRAMING_NEAREST_COUNT);
  }

  const lngs = [origin.lng, ...stores.map((store) => store.lng)];
  const lats = [origin.lat, ...stores.map((store) => store.lat)];
  return {
    stores,
    bounds: {
      west: Math.min(...lngs),
      south: Math.min(...lats),
      east: Math.max(...lngs),
      north: Math.max(...lats),
    },
    filtered,
    noMatch,
  };
}

/**
 * Hauteur (px) du marqueur d'un magasin isolé : la goutte 30×36 est ancrée par
 * sa pointe (`icon-anchor: bottom`, chantier store-locator-pin-concept), donc
 * elle occupe de la coordonnée du magasin jusqu'à 36 px au-dessus.
 */
const MARKER_HEIGHT = 36;

/**
 * Marges du cadrage, pour qu'aucun magasin retenu ne passe sous la légende
 * (en haut à gauche, 220 px + 12 px de décalage, ~176 px de bas en bas) ni sous
 * les boutons de la carte (en haut à droite, 36 px + 12 px de décalage) ni sous
 * le bouton « Voir la carte en plein écran » (aperçu mobile, en haut à gauche,
 * 12 px de décalage + ~30 px de haut), et sans recouvrir le logo Mapbox (en bas
 * à gauche, ~30 px de haut avec son décalage). Un magasin cadré se place au bord
 * de la marge ; celle-ci tient donc compte de la forme de son repère :
 * - un magasin isolé (marqueur 30×36 posé par sa pointe sur la coordonnée)
 *   occupe 15 px de chaque côté et 36 px AU-DESSUS de la coordonnée : la marge
 *   haute ajoute 36 px à l'élément à éviter (MARKER_HEIGHT) ;
 * - une pastille de regroupement reste centrée sur la coordonnée (rayon 20 px
 *   jusqu'à 9 magasins) : marges basse, gauche et droite gardent le demi-repère
 *   de 20 px, le plus large des deux besoins (la pointe, elle, n'a pas besoin
 *   de place sous la coordonnée).
 * - bureau (légende visible, carte large) : légende à gauche, boutons à droite
 *   (rien en haut, marge haute inchangée) ;
 * - aperçu mobile (pas de légende, 260 px de haut) : bouton plein écran en haut
 *   à gauche (12 + 30 + 2 px de jeu + marqueur 36 = 80), boutons à droite, logo
 *   Mapbox en bas ;
 * - plein écran mobile (légende visible, carte étroite) : légende en haut
 *   (176,1 + 2 px de jeu + marqueur 36 = 214). L'ancienne marge de 170 px datait
 *   d'une légende de ~150 px de haut ; elle laissait déjà passer des repères sous
 *   la légende actuelle (176 px).
 * Si les marges dépassent la place disponible, elles sont réduites
 * proportionnellement : le cadrage a toujours lieu (jamais d'absence de
 * cadrage faute de place).
 * @param  {{width: number, height: number, legendVisible: boolean}} params Taille de la carte en pixels.
 * @returns {{top: number, bottom: number, left: number, right: number}}
 */
export function computeFramingPadding({ width, height, legendVisible }) {
  let padding;
  if (legendVisible && width >= 600) {
    padding = { top: 60, bottom: 40, left: 260, right: 80 };
  } else if (legendVisible) {
    padding = { top: 178 + MARKER_HEIGHT, bottom: 40, left: 30, right: 80 };
  } else {
    padding = { top: 12 + 30 + 2 + MARKER_HEIGHT, bottom: 52, left: 30, right: 80 };
  }

  const MAX_SHARE = 0.8;
  const horizontal = padding.left + padding.right;
  if (width > 0 && horizontal > width * MAX_SHARE) {
    const ratio = (width * MAX_SHARE) / horizontal;
    padding.left = Math.floor(padding.left * ratio);
    padding.right = Math.floor(padding.right * ratio);
  }
  const vertical = padding.top + padding.bottom;
  if (height > 0 && vertical > height * MAX_SHARE) {
    const ratio = (height * MAX_SHARE) / vertical;
    padding.top = Math.floor(padding.top * ratio);
    padding.bottom = Math.floor(padding.bottom * ratio);
  }
  return padding;
}
